import React, {useContext, useEffect, useMemo, useRef, useState} from 'react'
import classNames from 'classnames'
import {Input} from 'antd'
import {BsCheck2} from 'react-icons/bs'
import {IoClose} from 'react-icons/io5'
import {PiListBulletsBold, PiLockKeyFill, PiNotePencilFill, PiTextBBold, PiTextItalic} from 'react-icons/pi'
import useTranslate from 'modules/local/useTranslate'
import useAsync from 'modules/asyncCache/useAsync'
import useAsyncAction from 'modules/asyncCache/useAsyncAction'
import {getResponseItems} from 'views/Shared'
import {bindQueryParam} from 'views/AppLocals/functions/routerHelper'
import {formatRelativeTime} from 'helpers/dateUtils'
import {timetableDetailUserNote_getByCourseClass_Api, timetableDetailUserNote_save_Api,} from 'apis'
import {LayoutContext} from 'components/layouts/Default/LayoutContext'
import DrawerPopupDialog from 'components/drawer/dialog/DrawerPopupDialog'

// Auto-save: gõ -> chờ im 8s rồi upsert. Flush thêm khi đóng/rời component để không mất chữ.
// 8s nằm trong khoảng 7-10s theo yêu cầu. content rỗng -> BE tự xoá dòng.
const AUTOSAVE_DEBOUNCE_MS = 8000

// Nội dung vẫn là TEXT THUẦN (field lưu ở BE là plain string, không phải rich content) - toolbar
// B/I/List bên dưới chỉ CHÈN KÝ HIỆU markdown-style (**đậm**, *nghiêng*, "- mục") vào đúng chuỗi text
// đó, không đổi sang định dạng có cấu trúc (JSON/Slate...) - giữ đúng data hiện có, không cần đổi API
// lưu/đọc hay chỗ hiển thị preview (line-clamp) nào khác.
const MAX_LENGTH = 1000

// ─── Hook: nạp 1 lượt toàn bộ ghi chú riêng của user cho 1 lớp, map theo timetable_detail_id ──
export const useMySessionNotes = (course_class_id) => {
    const [refreshKey, setRefreshKey] = useState(0)
    const {response, isLoading} = useAsync({
        apiInfo: timetableDetailUserNote_getByCourseClass_Api,
        query: bindQueryParam({course_class_id}),
        deps: [course_class_id, refreshKey],
    })
    const noteByDetailId = useMemo(() => {
        const map = {}
        ;(getResponseItems(response) ?? []).forEach((n) => {
            if (n?.timetable_detail_id) map[n.timetable_detail_id] = n
        })
        return map
    }, [response])
    return {noteByDetailId, isLoading, refresh: () => setRefreshKey((k) => k + 1)}
}

// ─── Ô ghi chú riêng cho 1 buổi học (dùng trong thẻ buổi học + tab tổng hợp ở /edu) ──────────
// Trước đây là 1 ô textarea nằm sẵn trong thẻ buổi học (autosave, không cần bấm Lưu) - trải nghiệm
// gõ-là-lưu tốt nhưng chiếm hẳn 1 khối cao trong card dù chưa gõ gì (bạn báo "nhiều khung quá" ở
// card cha) - giờ thu gọn về 1 NÚT (preview 1 dòng nội dung/placeholder), bấm mới mở khung nhập:
// mobile mở FULL MÀN HÌNH (Đóng/Lưu cố định ở thanh trên cùng, textarea chiếm hết phần còn lại - dễ
// gõ dài trên bàn phím mobile hơn hẳn 1 ô nhỏ giữa danh sách), desktop vẫn chỉ cần popup nhỏ (đủ
// chỗ, không cần chiếm hết màn hình). Autosave debounce GIỮ NGUYÊN bên trong khung nhập (trải nghiệm
// cũ vẫn tốt), CHỈ THÊM nút Lưu/Đóng tường minh cho người muốn chủ động thay vì chờ debounce.
// readOnly: buổi học gốc đã bị xoá/sinh lại (session.is_orphan) - chỉ xem lại nội dung cũ.
export const SessionPrivateNote = ({timetable_detail_id, initialNote, onChangedServer, readOnly = false}) => {
    const t = useTranslate()
    const {isSm, breakpoint} = useContext(LayoutContext)
    const isMobile = !!isSm || breakpoint === 'sm'
    const [isOpen, setIsOpen] = useState(false)
    const [value, setValue] = useState(initialNote?.content ?? '')
    // idle | dirty | saving | saved | error
    const [status, setStatus] = useState('idle')
    const latestRef = useRef(value)
    const savedRef = useRef(initialNote?.content ?? '')
    const timerRef = useRef(null)
    const textAreaRef = useRef(null)

    const {handleAsyncAction: doSave} = useAsyncAction({
        apiInfo: timetableDetailUserNote_save_Api,
        onSuccess: () => {
            savedRef.current = latestRef.current
            setStatus((s) => (s !== 'saving' ? s : latestRef.current.trim() === '' ? 'idle' : 'saved'))
            onChangedServer && onChangedServer()
        },
        onError: () => setStatus('error'),
    })

    // saveRef trỏ tới hàm lưu "mới nhất" - tránh phụ thuộc doSave (đổi ref mỗi lần isLoading đổi).
    const saveRef = useRef(() => {})
    saveRef.current = () => {
        if (!timetable_detail_id) return
        if (latestRef.current === savedRef.current) return
        setStatus('saving')
        doSave({content: latestRef.current}, {timetable_detail_id})
    }

    const schedule = () => {
        if (timerRef.current) clearTimeout(timerRef.current)
        timerRef.current = setTimeout(() => saveRef.current(), AUTOSAVE_DEBOUNCE_MS)
    }
    const flushNow = () => {
        if (timerRef.current) clearTimeout(timerRef.current)
        saveRef.current()
    }

    useEffect(() => () => flushNow(), []) // eslint-disable-line react-hooks/exhaustive-deps

    // applyValue: nguồn DUY NHẤT cập nhật `value` (gõ trực tiếp lẫn 3 nút định dạng bên dưới đều gọi
    // qua đây) - đảm bảo autosave/status luôn nhất quán bất kể chữ đến từ đâu.
    const applyValue = (next) => {
        if (next.length > MAX_LENGTH) next = next.slice(0, MAX_LENGTH)
        setValue(next)
        latestRef.current = next
        setStatus(next === savedRef.current ? 'idle' : 'dirty')
        schedule()
    }

    const handleChange = (e) => applyValue(e.target.value)

    const handleSave = () => {
        flushNow()
        setIsOpen(false)
    }
    const handleClose = () => {
        flushNow() // giữ đúng hành vi cũ - đóng khung nhập vẫn không mất chữ chưa kịp debounce
        setIsOpen(false)
    }

    // getNativeTextArea: antd Input.TextArea forward ref ra wrapper, textarea thật nằm ở
    // resizableTextArea.textArea - cần DOM node thật để đọc/đặt vị trí con trỏ (selectionStart/End).
    const getNativeTextArea = () => textAreaRef.current?.resizableTextArea?.textArea

    // wrapSelection: chèn markdown-style marker quanh vùng đang bôi đen (B/I) - không bôi đen thì
    // chèn placeholder ở giữa 2 marker rồi để con trỏ nằm giữa, gõ tiếp luôn.
    const wrapSelection = (marker) => {
        const el = getNativeTextArea()
        if (!el || readOnly) return
        const start = el.selectionStart ?? value.length
        const end = el.selectionEnd ?? value.length
        const selected = value.slice(start, end)
        const inner = selected || t('text')
        const next = value.slice(0, start) + marker + inner + marker + value.slice(end)
        if (next.length > MAX_LENGTH) return
        applyValue(next)
        requestAnimationFrame(() => {
            el.focus()
            const from = start + marker.length
            el.setSelectionRange(from, from + inner.length)
        })
    }

    // applyListPrefix: thêm "- " đầu mỗi dòng trong vùng bôi đen (hoặc dòng hiện tại nếu chưa bôi gì).
    const applyListPrefix = () => {
        const el = getNativeTextArea()
        if (!el || readOnly) return
        const start = el.selectionStart ?? value.length
        const end = el.selectionEnd ?? value.length
        const lineStart = value.lastIndexOf('\n', start - 1) + 1
        const nextBreak = value.indexOf('\n', end)
        const lineEnd = nextBreak === -1 ? value.length : nextBreak
        const block = value.slice(lineStart, lineEnd)
        const prefixed = block
            .split('\n')
            .map((line) => (line.startsWith('- ') ? line : `- ${line}`))
            .join('\n')
        const next = value.slice(0, lineStart) + prefixed + value.slice(lineEnd)
        if (next.length > MAX_LENGTH) return
        applyValue(next)
        requestAnimationFrame(() => {
            el.focus()
            el.setSelectionRange(lineStart, lineStart + prefixed.length)
        })
    }

    // headerSubtitle: dòng phụ dưới tiêu đề (cả header desktop lẫn mobile dùng chung) - LUÔN là chữ
    // thuần, không kèm chấm màu (chấm màu chỉ còn ở footer "auto-sync", xem syncNode).
    const headerSubtitle = readOnly
        ? t('session removed - note kept for reference')
        : {
            saving: t('saving'),
            error: t('save failed, retrying on next edit'),
            dirty: t('unsaved changes'),
        }[status]
        || (initialNote?.updated_at ? formatRelativeTime(initialNote.updated_at) : t('no changes yet'))

    // syncNode: chỉ dùng ở footer - chấm màu + nhãn "Tự động đồng bộ" (trạng thái ổn định, autosave
    // vẫn đang hoạt động) - đổi màu/chữ tạm thời khi đang lưu/lỗi, các trạng thái còn lại (idle/dirty/
    // saved) đều quy về cùng 1 nhãn vì với người dùng, chúng đều có nghĩa "cứ gõ, hệ thống tự lưu".
    const syncDotClass = readOnly
        ? 'bg-color-300'
        : status === 'saving' ? 'bg-amber-400 animate-pulse'
            : status === 'error' ? 'bg-red-500'
                : 'bg-green-500'
    const syncNode = (
        <span className="flex items-center gap-1.5 text-2xs text-color-400 flex-shrink-0">
            <span className={classNames('w-1.5 h-1.5 rounded-full flex-shrink-0', syncDotClass)}/>
            {status === 'saving' ? t('saving') : status === 'error' ? t('save failed, retrying on next edit') : t('auto-sync')}
        </span>
    )

    const charCount = value.length
    const wordCount = value.trim() === '' ? 0 : value.trim().split(/\s+/).length

    const toolbar = (
        <div className="flex items-center justify-between gap-2 pb-2 border-b border-color-50">
            <div className="flex items-center gap-1">
                <button
                    type="button" disabled={readOnly} onClick={() => wrapSelection('**')}
                    title={t('bold')}
                    className="w-7 h-7 flex flex-center rounded-md hover:background-100 border-0 text-color-400 cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed">
                    <PiTextBBold size={15}/>
                </button>
                <button
                    type="button" disabled={readOnly} onClick={() => wrapSelection('*')}
                    title={t('italic')}
                    className="w-7 h-7 flex flex-center rounded-md hover:background-100 border-0 text-color-400 cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed">
                    <PiTextItalic size={15}/>
                </button>
                <button
                    type="button" disabled={readOnly} onClick={applyListPrefix}
                    title={t('bulleted list')}
                    className="w-7 h-7 flex flex-center rounded-md hover:background-100 border-0 text-color-400 cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed">
                    <PiListBulletsBold size={15}/>
                </button>
                <span className="w-px h-4 background-200 mx-1"/>
            </div>
            <span className="text-2xs text-color-300 tracking-wide flex-shrink-0">
                {charCount}/{MAX_LENGTH} {t('characters')}
            </span>
        </div>
    )

    const textArea = (
        <Input.TextArea
            ref={textAreaRef}
            autoFocus
            autoSize={isMobile ? false : {minRows: 5, maxRows: 12}}
            value={value}
            onChange={handleChange}
            disabled={readOnly}
            maxLength={MAX_LENGTH}
            placeholder={t('write your private note for this session')}
            className={classNames(
                'w-full text-sm border border-color-100 rounded-xl focus:border-secondary-400 hover:border-secondary-300',
                isMobile ? 'flex-1' : ''
            )}
            style={isMobile ? {resize: 'none', height: '100%'} : {resize: 'none'}}
        />
    )

    const editorBody = (
        <div className="flex flex-col gap-3 h-full">
            {!isMobile && toolbar}
            {textArea}
            {!isMobile && (
                <div className="flex items-center justify-between gap-2 pt-1 border-t border-color-50">
                    {syncNode}
                    <div className="flex items-center gap-2 flex-shrink-0">
                        <button
                            type="button" onClick={handleClose}
                            className="px-3 py-1.5 rounded-lg text-xs font-semibold text-color-400 hover:background-100 border-0 cursor-pointer">
                            {t('close')}
                        </button>
                        <button
                            type="button" onClick={handleSave} disabled={readOnly}
                            className="flex items-center gap-1 px-4 py-1.5 rounded-lg text-xs font-bold text-white bg-secondary-500 hover:bg-secondary-600 border-0 cursor-pointer">
                            <BsCheck2 size={12}/>
                            {t('save')}
                        </button>
                    </div>
                </div>
            )}
            {isMobile && (
                <div className="flex items-center justify-between gap-2 flex-shrink-0">
                    <span className="text-2xs text-color-300">{wordCount} {t('words')}</span>
                    <span className="text-2xs text-color-300">{charCount} {t('characters')}</span>
                </div>
            )}
        </div>
    )

    // HeaderTitleBlock: icon vuông bo góc + tiêu đề + dòng phụ trạng thái - dùng CHUNG cho cả header
    // desktop (title của Modal) lẫn header mobile (thanh cố định trên cùng), tránh viết trùng 2 lần.
    const headerTitleBlock = (
        <div className="flex items-center gap-2.5 min-w-0">
            <div className="w-9 h-9 rounded-xl bg-secondary-50 flex flex-center flex-shrink-0">
                <PiNotePencilFill size={17} className="text-secondary-500"/>
            </div>
            <div className="flex flex-col min-w-0 leading-tight">
                <span className="font-bold text-sm text-color-000 truncate">{t('my private note')}</span>
                <span className="text-2xs text-color-400 truncate">{headerSubtitle}</span>
            </div>
        </div>
    )

    return (
        <>
            <button
                type="button"
                onClick={() => setIsOpen(true)}
                className="p-2.5 background-50 hover:background-100 rounded-xl flex flex-col gap-1 text-left w-full border-0 cursor-pointer transition-colors">
                <div className="flex flex-wrap items-center gap-1.5">
                    <PiNotePencilFill size={12} className="text-secondary-500 flex-shrink-0"/>
                    <span className="text-xs font-semibold text-color-300">{t('my private note')}</span>
                    <span className="flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-slate-100 text-2xs font-medium text-slate-400 whitespace-no-wrap">
                        <PiLockKeyFill size={10}/>
                        {t('only you')}
                    </span>
                    <span className="ml-auto text-2xs text-color-400">{headerSubtitle}</span>
                </div>
                <span className={classNames('text-xs leading-snug break-words line-clamp-2', value ? 'text-color-200' : 'text-color-300 italic')}>
                    {value || t('write your private note for this session')}
                </span>
            </button>

            {/* Mobile: overlay FULL MÀN HÌNH (thay bottom-sheet chung DrawDialog - bạn yêu cầu riêng
                khung này to hơn, dễ gõ note dài) - thanh trên cùng cố định Đóng (trái)/tiêu đề/Lưu
                (phải), textarea chiếm hết phần còn lại. */}
            {isMobile && isOpen && (
                <div className="fixed inset-0 background flex flex-col" style={{zIndex: 1000}}>
                    <div className="flex items-center justify-between gap-2 px-3 py-3 border-b border-color-100 flex-shrink-0">
                        <button
                            type="button" onClick={handleClose}
                            className="w-9 h-9 flex flex-center rounded-full hover:background-100 border-0 text-color-400 cursor-pointer flex-shrink-0">
                            <IoClose size={20}/>
                        </button>
                        {headerTitleBlock}
                        <button
                            type="button" onClick={handleSave} disabled={readOnly}
                            className="px-3 py-1.5 rounded-lg text-xs font-bold text-white bg-secondary-500 hover:bg-secondary-600 border-0 cursor-pointer flex-shrink-0">
                            {t('save')}
                        </button>
                    </div>
                    <div className="flex-1 min-h-0 p-3">
                        {editorBody}
                    </div>
                </div>
            )}

            {/* Desktop: popup nhỏ như mọi khung sửa nhanh khác của buổi học (SessionNotesDrawer...). */}
            {!isMobile && (
                <DrawerPopupDialog
                    title={headerTitleBlock}
                    width={480}
                    footer={false}
                    open={isOpen}
                    setVisible={(v) => (v ? setIsOpen(true) : handleClose())}>
                    {() => isOpen && editorBody}
                </DrawerPopupDialog>
            )}
        </>
    )
}
